import { useState } from 'react';

import { types } from './shared';
import { tryParseJSON } from '../../utilities';
import { useAppContext } from '../../molecules';
import { Button, SubmitButton } from '../../components';
import { Modal, Form, FormItem } from '../../components';
import { Input, Dropdown, Toggle } from '../../components';
import { useModelMeta } from './useModelMeta';
import type { Item, FormType } from './usePrompts';

type PromptModalProps = {
  initialValues?: Item;
  loading: boolean;
  campaignId: string;

  onSumbit?: (values: Item) => void;
  onClose: () => void;
};

const convertToFormType = (item?: Partial<Item>): FormType => {
  if (!item) return {};

  return {
    ...item,
    model_params: item.model_params ? JSON.stringify(item.model_params) : '',
  };
};

const convertToItemType = (form: FormType): Item => {
  const parsedModelParams = tryParseJSON(form.model_params);

  return {
    ...(form as Item),
    model_params:
      typeof parsedModelParams === 'object' ? parsedModelParams : null,
  };
};

export const PromptModal: React.FC<PromptModalProps> = ({
  initialValues,
  campaignId,
  loading: loadingItem,
  onSumbit,
  onClose,
}) => {
  const { meta } = useAppContext();
  const [item, handleChange] = useState<FormType>(() =>
    initialValues
      ? convertToFormType(initialValues)
      : { is_active: 'true', campaign_id: campaignId }
  );

  const {
    isDriving,
    loadingMeta,
    modelValue,
    testStatus,
    defaultModel,
    showFallbackHint,
    handleTestModel,
  } = useModelMeta(initialValues, item, handleChange);

  const loading = loadingItem || loadingMeta;

  const handleSubmit = () => {
    if (!item) return;

    const payload: FormType = isDriving ? item : { ...item, model: null };
    onSumbit?.(convertToItemType(payload));
  };

  const submitDisabled = isDriving && !modelValue;

  return (
    <Form initialValue={item} onChange={handleChange}>
      <Modal
        open
        size="big"
        title={initialValues ? 'Edit Prompt' : 'Add Prompt'}
        onClose={onClose}
        bottomActions={
          <>
            <SubmitButton
              label="Save"
              type="primary"
              loading={loading}
              disabled={submitDisabled}
              onClick={handleSubmit}
            />
            <Button onClick={onClose}>Cancel</Button>
          </>
        }
      >
        <div className="flex flex-col gap-y-md">
          <FormItem<NonNullable<typeof item>> name="name" label="Name" required>
            <Input />
          </FormItem>
          {!initialValues?.campaign_id && (
            <FormItem<NonNullable<typeof item>>
              required
              label="Campaign"
              name="campaign_id"
            >
              <Dropdown options={meta.campaigns} />
            </FormItem>
          )}
          <FormItem<NonNullable<typeof item>>
            required
            label="Type"
            name="prompt_type"
          >
            <Dropdown options={types.filter(o => !!o.value)} />
          </FormItem>
          {isDriving && (
            <div className="flex flex-col gap-y-xs">
              <FormItem<NonNullable<typeof item>>
                required
                name="model"
                label="LLM Model (OpenRouter)"
              >
                <Input placeholder="openai/gpt-5.2" />
              </FormItem>
              <FormItem<NonNullable<typeof item>>
                required={false}
                name="model_params"
                label="Model Params (JSON)"
              >
                <Input placeholder="JSON" multiline />
              </FormItem>
              <div className="flex items-center gap-x-sm">
                <Button
                  onClick={handleTestModel}
                  disabled={!modelValue || testStatus.state === 'pending'}
                >
                  {testStatus.state === 'pending' ? 'Testing...' : 'Test model'}
                </Button>
                {testStatus.state === 'ok' && (
                  <span className="text-success">✓ Model works</span>
                )}
                {testStatus.state === 'error' && (
                  <span className="text-danger">✗ {testStatus.message}</span>
                )}
              </div>
              {showFallbackHint && defaultModel && (
                <span className="text-warning">
                  Using default model: {defaultModel}
                </span>
              )}
            </div>
          )}
          <FormItem<NonNullable<typeof item>>
            name="description"
            label="Description"
            required
          >
            <Input />
          </FormItem>
          <FormItem<NonNullable<typeof item>>
            name="content"
            label="Content"
            required
          >
            <Input multiline rows={16} />
          </FormItem>
          <FormItem<NonNullable<typeof item>>
            name="version"
            label="Version"
            required
          >
            <Input />
          </FormItem>
          <FormItem<NonNullable<typeof item>>
            name="is_active"
            label="Active"
            required
          >
            <Toggle />
          </FormItem>
        </div>
      </Modal>
    </Form>
  );
};
